import { Box, Grid, Switch, Tooltip, Typography } from "@material-ui/core";
import ColumnSelect, { Column } from "~/components/adTable/columnSelect";
import { Filter, Range } from "~/typedef/store";
import Panel, { FooterLink } from "~/components/panel/panel";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  getCurrencyByCountryCode,
  getExchangeRate,
} from "~/utils/currencyUtils";

import ACOSCell from "~/components/table/cells/acosCell";
import { CurrentStore } from "~/typedef/store";
import { DATETIME_PERIODS } from "~/store/utils/dateTimeUtils";
import DownloadCsv from "../reportDownload/downloadCsv";
import { InlineIconButton } from "~/icons/inlineIconButton";
import { PERSISTENT_TABLE_IDS } from "~/utils/persistentTableIdsEnum";
import PanelLoading from "~/components/loadingIndicator/panelLoadingIndicator";
import ProductsSelector from "~/components/select/productsSelector";
import TACOSCell from "~/components/table/cells/tacosCell";
import Table from "~/components/adTable/table";
import { TextCell } from "~/components/table/cells/textCell";
import { ValueAndGrowthCell } from "~/components/table/cells/valueAndGrowthCell";
import { ValueCell } from "~/components/table/cells/valueCell";
import { formatCurrency } from "~/utils/currencyUtils";
import { getPercentageDifference } from "~/utils/salesUtils";
import { isUndefined } from "lodash";
import moment from "moment";
import { numberWithCommas } from "~/utils/utils";
import { usePersistentTableColumns } from "~/utils/usePersistentTableColumns";
import { useSalesByPeriodQuery } from "@store/mystore/salesByPeriod.redux";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

interface SalesByIntervalProps {
  store: CurrentStore;
  currentPeriod: DATETIME_PERIODS;
  currentRange: Range;
  currentCurrency: string;
  currentFilter?: Filter;
  footerLink?: FooterLink;
  timezone: string;
  includeTax: boolean;
  conditionalFormatting?: boolean;
}

const SalesByIntervalWidget = memo<SalesByIntervalProps>(
  function SalesByIntervalWidget({
    store,
    currentRange,
    currentCurrency,
    includeTax,
    timezone,
  }) {
    const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
    const { t } = useTranslation();
    const [intervalSortDesc, setIntervalSortDesc] = useState(true);
    const [showComparison, setShowComparison] = useState(false);
    const [activeColumns, setActiveColumns] = useState<string[]>([]);

    const [selectAll, setSelectAll] = useState(false);

    const currencyRates = useTypedSelector((state) =>
      state.globalVar ? state.globalVar.currencyRates : []
    );

    const handleFetchData = useCallback(({ sortBy }) => {
      // Only handle sorting for timeIntervals column
      if (sortBy && sortBy.length > 0 && sortBy[0].id === "timeIntervals") {
        setIntervalSortDesc(sortBy[0].desc);
      }
    }, []);

    const exchangeRate = getExchangeRate(
      currencyRates,
      // In case of multi-channel, generic returns the data in AUD (DESTINATION_CURRENCY)
      getCurrencyByCountryCode[store.marketplaceCountry],
      currentCurrency
    );

    const {
      salesByPeriod,
      currency,
      salesByPeriodFetching,
      salesByPeriodCount,
    } = useSalesByPeriodQuery(
      {
        mid: store.merchantId,
        currentRange,
        marketplaceFilters: {
          marketplaceType: store.marketplace,
          marketplaceSubtype: store.marketplaceSubtype ?? store.marketplace,
        },
        includeTax,
        sellerSkus: selectedSkus,
      },
      {
        selectFromResult: ({ data, isFetching }) => {
          return {
            salesByPeriod: data?.chartData ?? [],
            currency: data?.currency,
            salesByPeriodFetching: isFetching,
            salesByPeriodCount: data?.chartData.length || 0,
          };
        },
      }
    );

    const salesPerformanceData = useMemo(() => {
      return (
        salesByPeriod?.map((row) => {
          const { current, prior } = row;
          const startDateStr = moment
            .unix(current.startTime)
            .tz(timezone)
            .format("D MMM YY");
          const endDataStr = moment
            .unix(current.endTime)
            .tz(timezone)
            .format("D MMM YY");
          const interval =
            startDateStr === endDataStr
              ? startDateStr
              : currentRange.interval === "w"
              ? `w/c. ${startDateStr}`
              : `${startDateStr} - ${endDataStr}`;
          return {
            timeIntervals: interval,
            sales:
              current?.sales != undefined
                ? formatCurrency(
                    current?.sales?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            priorSales:
              prior?.sales != undefined
                ? formatCurrency(
                    prior?.sales?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            lineItems:
              current?.lineItemCount != undefined
                ? current?.lineItemCount
                : "-",
            priorLineItems:
              prior?.lineItemCount != undefined ? prior?.lineItemCount : "-",
            unitsSold:
              current?.unitsSold != undefined ? current?.unitsSold : "-",
            priorUnitsSold:
              prior?.unitsSold != undefined ? prior?.unitsSold : "-",
            avgLineItemValue:
              current?.avgLineItemValue != undefined
                ? formatCurrency(
                    current?.avgLineItemValue?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            priorAvgLineItemValue:
              prior?.avgLineItemValue != undefined
                ? formatCurrency(
                    prior?.avgLineItemValue?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            pageViews:
              current?.pageViews != undefined ? current?.pageViews : "-",
            priorPageViews:
              prior?.pageViews != undefined ? prior?.pageViews : "-",
            sessions:
              current?.totalSessions != undefined
                ? current?.totalSessions
                : "-",
            priorSessions:
              prior?.totalSessions != undefined ? prior?.totalSessions : "-",
            sessionConversions:
              current?.unitsPerSession != undefined
                ? `${current?.unitsPerSession.toFixed(1)}%`
                : "-",
            priorSessionConversions:
              prior?.unitsPerSession != undefined
                ? `${prior?.unitsPerSession.toFixed(1)}%`
                : "-",
            pageViewsConversions:
              current?.unitsPerView != undefined
                ? `${current?.unitsPerView.toFixed(1)}%`
                : "-",
            priorPageViewsConversions:
              prior?.unitsPerView != undefined
                ? `${prior?.unitsPerView.toFixed(1)}%`
                : "-",
            buyBoxWinRate:
              current?.buyboxWinPercentage != undefined
                ? `${current?.buyboxWinPercentage.toFixed(1)}%`
                : "-",
            priorBuyBoxWinRate:
              prior?.buyboxWinPercentage != undefined
                ? `${prior?.buyboxWinPercentage.toFixed(1)}%`
                : "-",
            impressions:
              current?.impressions != undefined ? current?.impressions : "-",
            priorImpressions:
              prior?.impressions != undefined ? prior?.impressions : "-",
            ctr:
              current?.clickThroughRate != undefined
                ? `${current?.clickThroughRate.toFixed(1)}%`
                : "-",
            priorCtr:
              prior?.clickThroughRate != undefined
                ? `${prior?.clickThroughRate.toFixed(1)}%`
                : "-",
            adSales:
              current?.adSales != undefined
                ? formatCurrency(
                    current?.adSales?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            priorAdSales:
              prior?.adSales != undefined
                ? formatCurrency(
                    prior?.adSales?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            adSpend:
              current.adSpend != undefined
                ? formatCurrency(
                    current?.adSpend?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            priorAdSpend:
              prior?.adSpend != undefined
                ? formatCurrency(
                    prior?.adSpend?.toFixed(2),
                    currencyRates,
                    currency,
                    currentCurrency
                  )
                : "-",
            acos: {
              value:
                current?.acos != undefined
                  ? `${current?.acos.toFixed(1)}%`
                  : "-",
            },
            priorAcos: {
              value:
                prior?.acos != undefined ? `${prior?.acos.toFixed(1)}%` : "-",
            },
            tacos: {
              value:
                current?.tacos != undefined
                  ? `${current?.tacos.toFixed(1)}%`
                  : "-",
            },
            priorTacos: {
              value:
                prior?.tacos != undefined ? `${prior?.tacos.toFixed(1)}%` : "-",
            },
            salesChange: {
              value:
                current?.sales != undefined
                  ? formatCurrency(
                      current?.sales?.toFixed(2),
                      currencyRates,
                      currency,
                      currentCurrency
                    )
                  : "-",
              growth: prior?.sales
                ? getPercentageDifference(current.sales, prior.sales)
                : "N/A",
              hideValue: true,
            },
            lineItemsChange: {
              value:
                current?.lineItemCount != undefined
                  ? numberWithCommas(current?.lineItemCount)
                  : "-",
              growth: prior?.lineItemCount
                ? getPercentageDifference(
                    current?.lineItemCount,
                    prior?.lineItemCount
                  )
                : "N/A",
              hideValue: true,
            },
            unitsChange: {
              value:
                current?.unitsSold != undefined
                  ? numberWithCommas(current?.unitsSold)
                  : "-",
              growth: prior?.unitsSold
                ? getPercentageDifference(current?.unitsSold, prior?.unitsSold)
                : "N/A",
              hideValue: true,
            },
            avgLineItemValueChange: {
              value:
                current?.avgLineItemValue != undefined
                  ? formatCurrency(
                      current?.avgLineItemValue?.toFixed(2),
                      currencyRates,
                      currency,
                      currentCurrency
                    )
                  : "-",
              growth: prior?.avgLineItemValue
                ? getPercentageDifference(
                    current?.avgLineItemValue,
                    prior?.avgLineItemValue
                  )
                : "N/A",
              hideValue: true,
            },
            pageViewsChange: {
              value: current?.pageViews != undefined ? current?.pageViews : "-",
              growth: prior?.pageViews
                ? getPercentageDifference(current?.pageViews, prior?.pageViews)
                : "N/A",
              hideValue: true,
            },
            sessionsChange: {
              value:
                current?.totalSessions != undefined
                  ? current?.totalSessions
                  : "-",
              growth: prior?.totalSessions
                ? getPercentageDifference(
                    current?.totalSessions,
                    prior?.totalSessions
                  )
                : "N/A",
              hideValue: true,
            },
            sessionConversionsChange: {
              value:
                current?.unitsPerSession != undefined
                  ? current?.unitsPerSession
                  : "-",
              growth: prior?.unitsPerSession
                ? getPercentageDifference(
                    current?.unitsPerSession,
                    prior?.unitsPerSession,
                    true
                  )
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
            },
            pageViewsConversionsChange: {
              value:
                current?.unitsPerView != undefined
                  ? current?.unitsPerView
                  : "-",
              growth: prior?.unitsPerView
                ? getPercentageDifference(
                    current?.unitsPerView,
                    prior?.unitsPerView,
                    true
                  )
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
            },
            buyBoxWinRateChange: {
              value:
                current?.buyboxWinPercentage != undefined
                  ? current?.buyboxWinPercentage
                  : "-",
              growth: prior?.buyboxWinPercentage
                ? getPercentageDifference(
                    current?.buyboxWinPercentage,
                    prior?.buyboxWinPercentage,
                    true
                  )
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
            },
            impressionsChange: {
              value:
                current?.impressions != undefined ? current?.impressions : "-",
              growth: prior?.impressions
                ? getPercentageDifference(
                    current?.impressions,
                    prior?.impressions
                  )
                : "N/A",
              hideValue: true,
            },
            ctrChange: {
              value:
                current?.clickThroughRate != undefined
                  ? current?.clickThroughRate
                  : "-",
              growth: prior?.clickThroughRate
                ? getPercentageDifference(
                    current?.clickThroughRate,
                    prior?.clickThroughRate,
                    true
                  )
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
            },
            adSalesChange: {
              value: current?.adSales != undefined ? current?.adSales : "-",
              growth: prior?.adSales
                ? getPercentageDifference(current?.adSales, prior?.adSales)
                : "N/A",
              hideValue: true,
            },
            adSpendChange: {
              value: current?.adSpend != undefined ? current?.adSpend : "-",
              growth: prior?.adSpend
                ? getPercentageDifference(current?.adSpend, prior?.adSpend)
                : "N/A",
              hideValue: true,
              reverseFormatting: true,
            },
            acosChange: {
              value: current?.acos != undefined ? current?.acos : "-",
              growth: prior?.acos
                ? getPercentageDifference(current?.acos, prior?.acos, true)
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
              reverseFormatting: true,
            },
            tacosChange: {
              value: current?.tacos != undefined ? current?.tacos : "-",
              growth: prior?.tacos
                ? getPercentageDifference(current?.tacos, prior?.tacos, true)
                : "N/A",
              hideValue: true,
              customSuffix: "ppt",
              reverseFormatting: true,
            },
          };
        }) || []
      );
    }, [salesByPeriod, currentRange, timezone, currencyRates, currentCurrency]);

    const salesPerformanceTableData = useMemo(() => {
      return intervalSortDesc
        ? [...salesPerformanceData].reverse()
        : salesPerformanceData;
    }, [intervalSortDesc, salesPerformanceData]);

    const columns: Column[] = useMemo(
      () => [
        {
          Header: `${t("myStoresWidget.salesByInterval.timeIntervals")} ${t(
            `generic.interval.${currentRange.interval}`
          )}`,
          accessor: "timeIntervals",
          id: "timeIntervals",
          Cell: TextCell,
          isVisible: true,
          sticky: "left",
          divideRight: true,
          isSorted: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.sales"),
          accessor: "sales",
          id: "sales",
          align: "right",
          Cell: ValueCell,
          isVisible: true,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorSales",
          id: "priorSales",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("sales"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "salesChange",
          id: "salesChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("sales"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.lineItems"),
          accessor: "lineItems",
          id: "lineItems",
          align: "right",
          Cell: ValueCell,
          isVisible: true,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorLineItems",
          id: "priorLineItems",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("lineItems"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "lineItemsChange",
          id: "lineItemsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("lineItems"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.units"),
          accessor: "unitsSold",
          id: "unitsSold",
          align: "right",
          Cell: ValueCell,
          isVisible: true,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorUnitsSold",
          id: "priorUnitsSold",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("unitsSold"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "unitsChange",
          id: "unitsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("unitsSold"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.avgLineItemValue"),
          accessor: "avgLineItemValue",
          id: "avgLineItemValue",
          align: "right",
          Cell: ValueCell,
          isVisible: true,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorAvgLineItemValue",
          id: "priorAvgLineItemValue",
          align: "right",
          Cell: ValueCell,
          isVisible:
            showComparison && activeColumns.includes("avgLineItemValue"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "avgLineItemValueChange",
          id: "avgLineItemValueChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible:
            showComparison && activeColumns.includes("avgLineItemValue"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.pageViews"),
          accessor: "pageViews",
          id: "pageViews",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorPageViews",
          id: "priorPageViews",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("pageViews"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "pageViewsChange",
          id: "pageViewsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("pageViews"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.pageViewsConversions"),
          accessor: "pageViewsConversions",
          id: "pageViewsConversions",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorPageViewsConversions",
          id: "priorPageViewsConversions",
          align: "right",
          Cell: ValueCell,
          isVisible:
            showComparison && activeColumns.includes("pageViewsConversions"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "pageViewsConversionsChange",
          id: "pageViewsConversionsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible:
            showComparison && activeColumns.includes("pageViewsConversions"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.sessions"),
          accessor: "sessions",
          id: "sessions",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorSessions",
          id: "priorSessions",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("sessions"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "sessionsChange",
          id: "sessionsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("sessions"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.sessionConversions"),
          accessor: "sessionConversions",
          id: "sessionConversions",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorSessionConversions",
          id: "priorSessionConversions",
          align: "right",
          Cell: ValueCell,
          isVisible:
            showComparison && activeColumns.includes("sessionConversions"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "sessionConversionsChange",
          id: "sessionConversionsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible:
            showComparison && activeColumns.includes("sessionConversions"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.buyBoxWinRate"),
          accessor: "buyBoxWinRate",
          id: "buyBoxWinRate",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorBuyBoxWinRate",
          id: "priorBuyBoxWinRate",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("buyBoxWinRate"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "buyBoxWinRateChange",
          id: "buyBoxWinRateChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("buyBoxWinRate"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.impressions"),
          accessor: "impressions",
          id: "impressions",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorImpressions",
          id: "priorImpressions",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("impressions"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "impressionsChange",
          id: "impressionsChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("impressions"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.ctr"),
          accessor: "ctr",
          id: "ctr",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorCtr",
          id: "priorCtr",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("ctr"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "ctrChange",
          id: "ctrChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("ctr"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.adSales"),
          accessor: "adSales",
          id: "adSales",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorAdSales",
          id: "priorAdSales",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("adSales"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "adSalesChange",
          id: "adSalesChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("adSales"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.adSpend"),
          accessor: "adSpend",
          id: "adSpend",
          align: "right",
          Cell: ValueCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorAdSpend",
          id: "priorAdSpend",
          align: "right",
          Cell: ValueCell,
          isVisible: showComparison && activeColumns.includes("adSpend"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "adSpendChange",
          id: "adSpendChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("adSpend"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.acos"),
          accessor: "acos",
          id: "acos",
          align: "right",
          Cell: ACOSCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorAcos",
          id: "priorAcos",
          align: "right",
          Cell: ACOSCell,
          isVisible: showComparison && activeColumns.includes("acos"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "acosChange",
          id: "acosChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("acos"),
          disableSortBy: true,
          divideRight: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.tacos"),
          accessor: "tacos",
          id: "tacos",
          align: "right",
          Cell: TACOSCell,
          isVisible: false,
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.comparison"),
          accessor: "priorTacos",
          id: "priorTacos",
          align: "right",
          Cell: TACOSCell,
          isVisible: showComparison && activeColumns.includes("tacos"),
          disableSortBy: true,
        },
        {
          Header: t("myStoresWidget.salesByInterval.percentChange"),
          accessor: "tacosChange",
          id: "tacosChange",
          align: "right",
          Cell: ValueAndGrowthCell,
          isVisible: showComparison && activeColumns.includes("tacos"),
          disableSortBy: true,
          divideRight: true,
        },
      ],
      [
        currentRange.interval,
        intervalSortDesc,
        t,
        showComparison,
        activeColumns,
      ]
    );

    const { myColumns, setMyColumns } = usePersistentTableColumns({
      tableId: PERSISTENT_TABLE_IDS.SALES_BY_PERIOD,
      initialColumns: columns,
      report: false,
    });

    useEffect(() => {
      const activeList = myColumns
        .filter(
          (myColumn) =>
            myColumn.id &&
            myColumn.id !== "timeIntervals" &&
            myColumn.Header !== "Comparison" &&
            myColumn.Header !== "CHANGE %" &&
            myColumn.isVisible
        )
        .map((col) => col.id)
        .filter((id): id is string => !isUndefined(id));

      setActiveColumns((prev) => {
        const isEqual =
          prev.length === activeList.length &&
          prev.every((id) => activeList.includes(id));
        if (isEqual) return prev;
        return activeList;
      });
    }, [myColumns]);

    return (
      <Panel
        id="widget-sales-by-interval"
        title={t(`myStoresWidget.salesByInterval.mainTitle`)}
        actions={
          <>
            <Grid item>
              <Grid container wrap="nowrap" alignItems="center">
                <Typography variant="body2">
                  {t("salesByInterval.showComparison")}
                </Typography>
                <Tooltip title={t("salesByInterval.showComparisonTooltip")}>
                  <InlineIconButton />
                </Tooltip>
                <Switch
                  color="primary"
                  checked={showComparison}
                  onChange={() => setShowComparison(!showComparison)}
                />
              </Grid>
            </Grid>
            <Grid item xs={11} md={4}>
              <ProductsSelector
                title={t("salesByInterval.selectProducts")}
                onProductsChange={setSelectedSkus}
                mid={store.merchantId}
                marketplace={store.marketplace}
                selectAll={selectAll}
                setSelectAll={setSelectAll}
                initProducts={selectedSkus.map((sellerSku) => ({
                  sellerSku,
                }))}
              />
            </Grid>
            <DownloadCsv
              reportType="salesByPeriod"
              path="/api/generic/salesByPeriod"
              mid={store.merchantId}
              params={{
                mid: store.merchantId,
                currentRange,
                marketplaceFilters: {
                  marketplaceType: store.marketplace,
                  marketplaceSubtype:
                    store.marketplaceSubtype ?? store.marketplace,
                },
                includeTax,
                currency: currentCurrency,
                exchangeRate,
                sellerSkus: selectedSkus,
              }}
            />
            <ColumnSelect
              columns={myColumns.filter(
                (c) =>
                  c.Header !== "Comparison" &&
                  c.Header !== "CHANGE %" &&
                  c.id !== "timeIntervals"
              )}
              setColumns={setMyColumns}
            />
          </>
        }
        content={
          salesByPeriodFetching ? (
            <PanelLoading />
          ) : (
            <Box>
              <Table
                {...{
                  columns: myColumns,
                  data: salesPerformanceTableData,
                  fetchData: handleFetchData,
                  pagination: false,
                  sorting: true,
                  pageCount: 1,
                  pageSize: salesByPeriodCount,
                  loading: salesByPeriodFetching,
                }}
              />
            </Box>
          )
        }
      />
    );
  }
);

export default SalesByIntervalWidget;
